import { create } from 'zustand';
import { setMonedaDeLaEmpresa } from '@/lib/utils/moneda';
import {
  altaDeClienteService,
  type AjustesDelModuloDto,
  type CambiosDeAjustesDto,
} from '../services/alta-de-cliente-service';
import type { PrimerosPasosDto } from '../lib/siguiente-paso';

/**
 * L7 (vault 279): lo que la empresa ajustó del módulo, cargado una vez por sesión de
 * la página. Al cargar deja vigentes la moneda y los umbrales para los formateadores
 * que no son componentes (gráficos, celdas).
 */
interface ConfiguracionDelModuloState {
  ajustes: AjustesDelModuloDto | null;
  cargando: boolean;
  error: string | null;
  cargarAjustes: (force?: boolean) => Promise<void>;
  guardarAjustes: (cambios: CambiosDeAjustesDto) => Promise<void>;
  /** Qué le falta a la empresa (primeros pasos y estados vacíos). */
  primerosPasos: PrimerosPasosDto | null;
  /** Cuándo se leyeron (ms); 0 = nunca. */
  primerosPasosEn: number;
  /** Con `force`, aunque los leídos tengan menos de 30 s. */
  cargarPrimerosPasos: (force?: boolean) => Promise<void>;
}

/** Los de hoy, mientras no cargó la configuración (o si falló). */
const UMBRAL_DE_OPORTUNIDAD_INICIAL = 5;
const DIAS_DE_FRESCURA_INICIAL = 30;
/** Ronda E (T15-5): cuánto valen los contadores de primeros pasos antes de volver a pedirlos. */
export const PRIMEROS_PASOS_MS = 30_000;
let pidiendoPrimerosPasos: Promise<void> | null = null;

function aplicar(ajustes: AjustesDelModuloDto) {
  setMonedaDeLaEmpresa(
    ajustes.baseCurrency
      ? { code: ajustes.baseCurrency.code, symbol: ajustes.baseCurrency.symbol }
      : null
  );
}

export const useConfiguracionDelModulo = create<ConfiguracionDelModuloState>((set, get) => ({
  ajustes: null,
  cargando: false,
  error: null,
  cargarAjustes: async (force = false) => {
    if (!force && (get().ajustes || get().cargando)) return;
    set({ cargando: true, error: null });
    try {
      const ajustes = await altaDeClienteService.getAjustes();
      aplicar(ajustes);
      set({ ajustes, cargando: false });
    } catch {
      set({ cargando: false, error: 'No se pudieron leer los ajustes del módulo.' });
    }
  },
  guardarAjustes: async (cambios) => {
    const ajustes = await altaDeClienteService.guardarAjustes(cambios);
    aplicar(ajustes);
    set({ ajustes });
  },
  primerosPasos: null,
  primerosPasosEn: 0,
  cargarPrimerosPasos: (force = false) => {
    // Ronda E (T15-5): son 7 conteos; cambiar de pestaña no los repite. Lo que ya se
    // está pidiendo se comparte, y lo leído vale 30 s.
    if (pidiendoPrimerosPasos && !force) return pidiendoPrimerosPasos;
    if (!force && get().primerosPasos && Date.now() - get().primerosPasosEn < PRIMEROS_PASOS_MS)
      return Promise.resolve();
    pidiendoPrimerosPasos = (async () => {
      try {
        const primerosPasos = await altaDeClienteService.getPrimerosPasos();
        set({ primerosPasos, primerosPasosEn: Date.now() });
      } catch {
        // Sin contadores no se muestra ningún consejo: mejor nada que uno equivocado.
      } finally {
        pidiendoPrimerosPasos = null;
      }
    })();
    return pidiendoPrimerosPasos;
  },
}));

/** El umbral de «requiere acción» de Gráficos, de la empresa (o 5 % si todavía no cargó). */
export function umbralDeOportunidad(): number {
  return (
    useConfiguracionDelModulo.getState().ajustes?.opportunityThresholdPct ??
    UMBRAL_DE_OPORTUNIDAD_INICIAL
  );
}

/** Lo mismo, para un componente: re-dibuja cuando llega la configuración. */
export function useUmbralDeOportunidad(): number {
  return useConfiguracionDelModulo(
    (s) => s.ajustes?.opportunityThresholdPct ?? UMBRAL_DE_OPORTUNIDAD_INICIAL
  );
}

/** Cuántos días un precio de la competencia es reciente para la empresa (o 30). */
export function diasDeFrescura(): number {
  return useConfiguracionDelModulo.getState().ajustes?.freshnessDays ?? DIAS_DE_FRESCURA_INICIAL;
}

/** Lo mismo, para un componente. */
export function useDiasDeFrescura(): number {
  return useConfiguracionDelModulo((s) => s.ajustes?.freshnessDays ?? DIAS_DE_FRESCURA_INICIAL);
}
